import { HttpClient } from "@utils/index";
import type {
  TriggerEvaluationResponse,
  TriggerEvaluationViewModel,
  EvaluationListResponse,
  EvaluationListViewModel,
  EvaluationDetailResponse,
  EvaluationDetailViewModel,
} from "@models/evaluation";
import {
  buildTriggerEvaluationViewModel,
  buildEvaluationListViewModel,
  buildEvaluationDetailsViewModel,
} from "@utils/builders/evaluation";

const BASE_URL = import.meta.env.VITE_EXTRACTION_API_BASE_URL;

if (!BASE_URL) {
  throw new Error(
    "Missing required environment variable: VITE_EXTRACTION_API_BASE_URL. " +
      "Please check your .env file and ensure the Intelligence Engine API URL is set.",
  );
}

class EvaluationApi {
  private httpClient: HttpClient;

  constructor() {
    this.httpClient = new HttpClient(`${BASE_URL}`);
  }

  /**
   * Trigger evaluation process for a document type
   */
  async triggerEvaluation(
    doctype: string,
    flowBaseId?: string,
    flowVersion?: number,
  ): Promise<TriggerEvaluationViewModel> {
    const response =
      await this.httpClient.makeAuthenticatedRequest<TriggerEvaluationResponse>(
        `/evaluations`,
        {
          method: "POST",
          body: JSON.stringify({
            doctype,
            ...(flowBaseId && flowVersion !== undefined
              ? { flow_base_id: flowBaseId, flow_version: flowVersion }
              : {}),
          }),
        },
      );

    return buildTriggerEvaluationViewModel(response);
  }

  /**
   * List evaluation results with filtering and pagination
   */
  async listEvaluations(
    doctype: string,
    flowBaseId: string,
    flowVersion: number,
    limit: number = 20,
    lastEvaluatedKey?: string,
  ): Promise<EvaluationListViewModel> {
    const params = new URLSearchParams({
      doctype,
      flow_base_id: flowBaseId,
      flow_version: flowVersion.toString(),
      limit: limit.toString(),
    });

    if (lastEvaluatedKey) {
      params.append("last_evaluated_key", lastEvaluatedKey);
    }

    const response =
      await this.httpClient.makeAuthenticatedRequest<EvaluationListResponse>(
        `/evaluations?${params.toString()}`,
        {
          method: "GET",
        },
      );

    return buildEvaluationListViewModel(response);
  }

  /**
   * Get detailed evaluation information
   */
  async getEvaluationDetails(
    processId: string,
  ): Promise<EvaluationDetailViewModel[]> {
    const response = await this.httpClient.makeAuthenticatedRequest<
      EvaluationDetailResponse[]
    >(`/evaluations/${encodeURIComponent(processId)}`, {
      method: "GET",
    });

    return buildEvaluationDetailsViewModel(response);
  }
}

export { EvaluationApi };

export const evaluationApi = new EvaluationApi();
